/**
 * Holi rangoli — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_shivratri_holi`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const holiRangoliPack: ToilePack = {
    slug: 'holi-rangoli',
    category: 'faith',
    name: 'Holi rangoli',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'pots', label: 'Colour pots', subject: 'a round rangoli of five clay pots spilling pink, yellow, green, blue and orange powder towards the centre, with a flower at the middle'},
        {key: 'pichkari', label: 'Pichkari', subject: 'a rangoli of two crossed pichkaris spraying arcs of colour inside a ring of marigold petals'},
        {key: 'peacock', label: 'Peacock', subject: 'a rangoli of a peacock with its tail fanned into bands of every Holi colour, splashes of gulal in the corners'},
    ],
    styles: [
        {key: 'gulal', label: 'Bright gulal', style: 'laid in fine bright gulal powder, soft dusty edges and smooth heaped lines', palette: 'pink, yellow, green, blue and orange', framing: 'The design fills most of the square, centred on a plain dark grey stone floor.'},
        {key: 'petals', label: 'Flower petals', style: 'built from loose flower petals and leaves, each petal a visible small shape', palette: 'marigold orange, pink rose petals, yellow and green leaves', framing: 'The design is centred with an even margin on a plain pale cement floor.'},
        {key: 'chart', label: 'Drawn chart', style: 'a clean flat vector drawing with bold solid shapes and thin white separating lines, easy to copy by hand', palette: 'a limited palette of five colours: pink, yellow, green, blue and orange', framing: 'The design is centred with a wide margin on a plain white background.'},
    ],
    directions: ['a flower', 'school size', 'two colours'],
    directionHint: 'try: a flower',
    seo: {
        title: 'Holi rangoli: colour pots, pichkari, peacock',
        metaDescription: 'Nine Holi rangoli designs for the morning of the festival, in bright gulal, flower petals or a drawn chart, seen from above.',
        h1: 'Holi rangoli: colour pots, pichkari and peacock',
        tagline: 'Nine floor designs seen from above, three materials each: gulal, flower petals and a drawn chart.',
        intro: 'Rangoli for the morning of Holi, made in bright gulal at the door or in a school courtyard. Each design shows one subject: five clay pots spilling colour towards a flower, two crossed pichkaris inside a ring of marigold petals, or a peacock with its tail fanned into the colours of the festival. The drawn chart is easy to copy by hand. Type a different subject, such as a flower, and the same materials carry it.',
        useCases: [
            'A rangoli at the door on the morning of Holi',
            'A design for a school courtyard or a community hall',
            'A copy-by-hand chart for a rangoli class',
            'A design to photograph and share on the day',
        ],
        faq: [
            {q: 'What materials are shown?', a: 'Bright gulal powder, flower petals and leaves, and a flat drawn chart with a limited palette of five colours.'},
            {q: 'Can a beginner copy the drawn chart?', a: 'Yes. It uses bold solid shapes and thin white lines between them, which show where each colour goes.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. A subject you type is a request; the nine samples stay as shown.'},
        ],
    },
}
